/** Ui owns its state and behavior; dependencies are passed in by main. */
import { registerContactActions } from './contact.js';
import { bindProjectPaging } from './project-paging.js';
import { bindProjectRows } from './project-rows.js';
import { observeScrollPreviews } from './project-scroll-preview.js';

export function register(app) {
  const { ui, layout, preferences } = app;

  registerContactActions(app);

  ui.openMap = function openMap() {
    const studio = document.querySelector('.studio-map-link');
    if (studio) window.open(studio.href, '_blank', 'noopener,noreferrer');
  };

  ui.smoothScrollTo = function smoothScrollTo(targetPosition, duration) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo(0, targetPosition);
      return;
    }
    const maxScroll =
      Math.max(document.body.scrollHeight, document.documentElement.scrollHeight) -
      window.innerHeight;
    targetPosition = Math.min(targetPosition, maxScroll);
    targetPosition = Math.max(targetPosition, 0);
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    let startTime = null;
    function easeInOutCubic(t, b, c, d) {
      t /= d / 2;
      if (t < 1) return (c / 2) * t * t * t + b;
      t -= 2;
      return (c / 2) * (t * t * t + 2) + b;
    }
    function animation(currentTime) {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const run = easeInOutCubic(timeElapsed, startPosition, distance, duration);
      window.scrollTo(0, run);
      if (timeElapsed < duration) requestAnimationFrame(animation);
      else window.scrollTo(0, targetPosition);
    }
    requestAnimationFrame(animation);
  };

  ui.setCursorCircle = function setCursorCircle() {
    ui.cursor.classList.add('cursor-circle');
    ui.cursor.innerHTML = '';
  };

  ui.setCursorCross = function setCursorCross() {
    ui.cursor.classList.remove('cursor-circle');
    ui.cursor.innerHTML = ui.svgCross;
  };

  ui.shouldIgnoreArrowNavigation = function shouldIgnoreArrowNavigation(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return true;
    const target = event.target;
    if (!target) return false;
    if (ui.isTextEditable(target)) return true;
    if (
      target.closest(
        'dialog[open], input, textarea, select, [contenteditable="true"], .custom-select, .custom-select-panel, .custom-select-toggle, kubis-spolupraca, #about-journey',
      )
    )
      return true;
    return false;
  };

  ui.observeElements = function observeElements() {
    document.querySelectorAll('.project-card').forEach((card) => ui.observer.observe(card));
  };
}

/** Initializers are called explicitly, in dependency order, from main. */
export function initializeCursor(app) {
  const { ui, layout, preferences } = app;
  ui.cursor = document.getElementById('custom-cursor');
  ui.svgCross =
    '<svg viewBox="0 0 100 100" aria-hidden="true" fill="none"><line x1="18" y1="18" x2="82" y2="82" stroke="var(--cursor-icon-outline)" stroke-width="34" stroke-linecap="round"></line><line x1="82" y1="18" x2="18" y2="82" stroke="var(--cursor-icon-outline)" stroke-width="34" stroke-linecap="round"></line><line x1="18" y1="18" x2="82" y2="82" stroke="currentColor" stroke-width="18" stroke-linecap="round"></line><line x1="82" y1="18" x2="18" y2="82" stroke="currentColor" stroke-width="18" stroke-linecap="round"></line></svg>';
  ui.setCursorCircle();
  document.addEventListener('mousemove', (e) => {
    ui.cursor.style.left = e.clientX + 'px';
    ui.cursor.style.top = e.clientY + 'px';
  });
  document.addEventListener('mousedown', (e) => {
    ui.cursor.style.left = e.clientX + 'px';
    ui.cursor.style.top = e.clientY + 'px';
    ui.setCursorCross();
  });
  document.addEventListener('mouseup', (e) => {
    ui.cursor.style.left = e.clientX + 'px';
    ui.cursor.style.top = e.clientY + 'px';
    ui.setCursorCircle();
  });
  document.addEventListener('dragend', ui.setCursorCircle);
  document.addEventListener('drop', ui.setCursorCircle);
  window.addEventListener('blur', ui.setCursorCircle);
  document.addEventListener('mouseleave', ui.setCursorCircle);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) ui.setCursorCircle();
  });
  ui.textEditableSelector = 'input, textarea, select, [contenteditable="true"]';
  ui.isTextEditable = (el) => !!(el && el.closest && el.closest(ui.textEditableSelector));
  ui.enableTypingCursor = () => document.body.classList.add('typing-cursor');
  ui.disableTypingCursor = () => {
    if (!ui.isTextEditable(document.activeElement)) {
      document.body.classList.remove('typing-cursor');
    }
  };
  document.addEventListener('mouseover', (e) => {
    if (ui.isTextEditable(e.target)) ui.enableTypingCursor();
    else ui.disableTypingCursor();
  });
  document.addEventListener('focusin', (e) => {
    if (ui.isTextEditable(e.target)) ui.enableTypingCursor();
  });
  document.addEventListener('focusout', () => {
    setTimeout(ui.disableTypingCursor, 0);
  });
}

export function bindHoverEffects(app, root = document) {
  const links = root.querySelectorAll(
    'a, button, .collab-logo, .theme-toggle, .contact-item, .social-icon, .privacy-link, .contact-link, .mode-tab, .project-tab, .project-card, .note-card-link, .contact-jump-btn, .contact-route-card',
  );
  links.forEach((el) => {
    el.addEventListener('mouseenter', () => document.body.classList.add('hovering'));
    el.addEventListener('mouseleave', () => document.body.classList.remove('hovering'));
  });
}

/** SVG images have no native loading="lazy"; hydrate their sources close to the viewport. */
export function hydrateProjectPreview(preview) {
  preview.querySelectorAll('image[data-preview-src]').forEach((image) => {
    image.setAttribute('href', image.dataset.previewSrc);
    image.removeAttribute('data-preview-src');
  });
}

export function bindTabs(app) {
  const { ui, layout } = app;
  const arrangeBoard = bindProjectRows();
  let previewObserver;
  if (typeof IntersectionObserver !== 'undefined') {
    previewObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          hydrateProjectPreview(entry.target);
          previewObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '480px 0px', threshold: 0 },
    );
  }
  const observeScrollPreview = observeScrollPreviews();
  const observePreviews = (root) => {
    root.querySelectorAll?.('.project-preview').forEach((preview) => {
      if (previewObserver) previewObserver.observe(preview);
      else if (!preview.closest('[hidden]')) hydrateProjectPreview(preview);
      observeScrollPreview(preview);
    });
  };
  document.querySelectorAll('.home-project-board').forEach(observePreviews);
  ui.projectTabGroups = Array.from(document.querySelectorAll('.project-tab-group'));
  ui.projectTabGroups.forEach((group) => {
    const paging = bindProjectPaging(group, fragment => {
      observePreviews(fragment);
      bindHoverEffects(app, fragment);
      app.projects.initProjectCardHoverPanels(fragment);
      layout.queueDotGridZoneUpdate();
    });
    const projectTabs = Array.from(group.querySelectorAll('.project-tabs .project-tab'));
    const projectContents = Array.from(
      group.querySelectorAll('.project-content-wrapper .project-content'),
    );
    let filterRevision = 0;
    function activateProjectTab(targetId) {
      filterRevision += 1;
      projectTabs.forEach((tab) => {
        const selected = tab.dataset.target === targetId;
        tab.classList.toggle('active', selected);
        tab.setAttribute?.('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      projectContents.forEach((content) => {
        const selected = content.id === targetId;
        content.classList.toggle('active', selected);
        content.hidden = !selected;
        if (selected) {
          observePreviews(content);
          paging.openPanel(content);
        }
      });
      layout.queueDotGridZoneUpdate();
      setTimeout(layout.queueDotGridZoneUpdate, 360);
    }
    if (group.closest?.('#view-projects')) {
      ui.getArchiveSnapshot = () => {
        const panel = projectContents.find(content => content.classList.contains('active'));
        return panel ? { panelId: panel.id, count: panel.querySelector('.projects-grid').childElementCount } : null;
      };
      ui.restoreArchiveSnapshot = snapshot => {
        const panel = projectContents.find(content => content.id === snapshot?.panelId);
        if (!panel || !Number.isSafeInteger(snapshot.count) || snapshot.count < 0) return null;
        if (!panel.classList.contains('active')) activateProjectTab(panel.id);
        const grid = panel.querySelector('.projects-grid');
        if (grid.childElementCount >= snapshot.count) {
          // The viewport may have rotated while this grid was hidden behind the detail.
          arrangeBoard?.(grid);
          return null;
        }
        const revision = filterRevision;
        const stillCurrent = () => revision === filterRevision && app.navigation.requestedRoute === 'projects';
        return paging.restoreCount(panel, snapshot.count, stillCurrent).then(async restored => {
          if (!restored) return false;
          // Let the board's resize observer finish sizing the restored rows before locating the card.
          await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
          return stillCurrent();
        });
      };
    }
    projectTabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        const targetId = tab.dataset.target;
        if (targetId) activateProjectTab(targetId);
      });
      tab.addEventListener('keydown', (event) => {
        let nextIndex;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % projectTabs.length;
        else if (event.key === 'ArrowLeft')
          nextIndex = (index + projectTabs.length - 1) % projectTabs.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = projectTabs.length - 1;
        else return;
        event.preventDefault();
        event.stopPropagation();
        const next = projectTabs[nextIndex];
        activateProjectTab(next.dataset.target);
        next.focus();
      });
    });
    observePreviews(group);
  });
}

export function initializeRevealObserver(app) {
  const { ui, layout, preferences } = app;
  ui.observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('visible');
      });
    },
    { threshold: 0.12 },
  );
}
